'use client'

import { Suspense, useState } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Eye, EyeOff } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { RezaroLogo } from '@/app/(public)/components/rezaro-logo'
import { lp } from '../_home/locale-path'

type Lang = 'de' | 'en' | 'tr'

const T = {
  de: {
    welcome: 'Willkommen',
    back: 'zurück.',
    intro: 'Ihr Restaurant wartet. Reservierungen, Team und Berichte an einem Ort.',
    legal: ['AGB', 'Datenschutz', 'Impressum'] as const,
    social_google: 'Mit Google fortfahren',
    operatorPanel: 'Operatör Paneli →',
    noAccount: 'Kein Konto?',
    register: 'Registrieren',
    eyebrow: 'Anmelden',
    greeting: 'Guten Tag.',
    or: 'oder',
    emailLabel: 'E-Mail-Adresse',
    emailPlaceholder: 'ihre@email.de',
    passwordLabel: 'Passwort',
    forgot: 'Vergessen?',
    submit: 'Anmelden',
    submitting: 'Anmeldung läuft...',
    errSignin: 'Anmeldung fehlgeschlagen',
    errInvalid: 'E-Mail oder Passwort falsch.',
    errOauth: 'OAuth fehlgeschlagen',
  },
  en: {
    welcome: 'Welcome',
    back: 'back.',
    intro: 'Your restaurant is waiting. Reservations, team and reports in one place.',
    legal: ['Terms', 'Privacy', 'Imprint'] as const,
    social_google: 'Continue with Google',
    operatorPanel: 'Operator Panel →',
    noAccount: 'No account?',
    register: 'Sign up',
    eyebrow: 'Sign in',
    greeting: 'Good to see you.',
    or: 'or',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@email.com',
    passwordLabel: 'Password',
    forgot: 'Forgot?',
    submit: 'Sign in',
    submitting: 'Signing in...',
    errSignin: 'Sign-in failed',
    errInvalid: 'Email or password is incorrect.',
    errOauth: 'OAuth failed',
  },
  tr: {
    welcome: 'Tekrar',
    back: 'hoş geldiniz.',
    intro: 'Restoranınız sizi bekliyor. Rezervasyonlar, ekip ve raporlar tek bir yerde.',
    legal: ['Şartlar', 'Gizlilik', 'Künye'] as const,
    social_google: 'Google ile devam et',
    operatorPanel: 'Operatör Paneli →',
    noAccount: 'Hesabınız yok mu?',
    register: 'Kayıt olun',
    eyebrow: 'Giriş yap',
    greeting: 'Hoş geldiniz.',
    or: 'veya',
    emailLabel: 'E-posta adresi',
    emailPlaceholder: 'siz@email.com',
    passwordLabel: 'Parola',
    forgot: 'Unuttunuz mu?',
    submit: 'Giriş yap',
    submitting: 'Giriş yapılıyor...',
    errSignin: 'Giriş başarısız',
    errInvalid: 'E-posta veya parola hatalı.',
    errOauth: 'OAuth başarısız',
  },
} as const

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="#1877F2" className="h-4 w-4">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

// Facebook entry hidden until the FB app + Supabase provider creds
// are configured — clicking it was throwing "Provider not enabled".
// Re-add when /opt/rezaro/.env has FB_APP_ID + FB_APP_SECRET and
// Supabase Dashboard → Auth → Providers → Facebook is on.
const SOCIAL = [
  { id: 'google', Icon: GoogleIcon },
] as const

export default function SigninPage({ lang = 'de' }: { lang?: Lang }) {
  return (
    <Suspense fallback={<div className="min-h-screen bg-pub-bg" />}>
      <SigninForm lang={lang} />
    </Suspense>
  )
}

function SigninForm({ lang }: { lang: Lang }) {
  const t = T[lang]
  const router = useRouter()
  const searchParams = useSearchParams()
  const next = searchParams.get('next')
  const [showPw, setShowPw] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const supabase = createClient()
      const { data: signInData, error: err } = await supabase.auth.signInWithPassword({ email, password })
      if (err) throw err
      // Always resolve user's actual restaurant; only honor `next` if explicitly provided.
      let target = next ?? null
      if (!target && signInData.user?.id) {
        const { data: rest } = await supabase
          .from('restaurants')
          .select('slug')
          .eq('owner_id', signInData.user.id)
          .maybeSingle()
        target = rest?.slug ? `/${rest.slug}/bookings/day` : '/onboarding/complete-profile'
      }
      router.push(target ?? '/onboarding/complete-profile')
      router.refresh()
    } catch (err) {
      const msg = err instanceof Error ? err.message : t.errSignin
      setError(msg.includes('Invalid login credentials') ? t.errInvalid : msg)
    } finally {
      setLoading(false)
    }
  }

  async function handleSocial(provider: 'google' | 'facebook') {
    setError(null)
    try {
      const supabase = createClient()
      // Native app: Google forbids OAuth inside a WebView, so open the
      // provider in a system Custom Tab and return via the rezaro:// deep
      // link (handled in NativeBridge → exchangeCodeForSession).
      const cap = typeof window !== 'undefined' ? window.Capacitor : undefined
      if (cap?.isNativePlatform?.()) {
        const redirectTo = next
          ? `rezaro://auth/callback?next=${encodeURIComponent(next)}`
          : 'rezaro://auth/callback'
        const { data, error: nativeErr } = await supabase.auth.signInWithOAuth({
          provider,
          options: { redirectTo, skipBrowserRedirect: true },
        })
        if (nativeErr) throw nativeErr
        if (data?.url) await cap.Plugins?.Browser?.open({ url: data.url })
        return
      }
      // No `next` -> callback finds user's restaurant or sends to complete-profile
      const callbackUrl = next
        ? `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`
        : `${window.location.origin}/auth/callback`
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: callbackUrl },
      })
      if (err) throw err
    } catch (err) {
      const msg = err instanceof Error ? err.message : t.errOauth
      setError(msg)
    }
  }

  return (
    <div className="flex min-h-screen" style={{ fontFamily: 'var(--font-pub)' }}>

      {/* Left — decorative */}
      <div className="hidden lg:flex lg:w-2/5 flex-col justify-between p-12 bg-pub-dark">
        <div>
          <div className="mb-16">
            <RezaroLogo variant="dark" size="md" showWordmark showTagline />
          </div>
          <h2 className="text-4xl font-light text-white leading-snug mb-4"
            style={{ fontFamily: 'var(--font-display)' }}>
            {t.welcome}<br />
            <em className="not-italic font-semibold">{t.back}</em>
          </h2>
          <p className="text-[14px] text-white/40 max-w-xs leading-relaxed">
            {t.intro}
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-[12px] text-white/25">
          <span>© Rezaro</span>
          {t.legal.map((item) => (
            <a key={item} href="#" className="hover:text-white/60 transition-colors">{item}</a>
          ))}
        </div>
      </div>

      {/* Right — form. Centering via my-auto (NOT justify-center): with
          justify-center a form taller than the viewport — small phones,
          open keyboard, banners — gets clipped at the TOP with no way to
          scroll up ("Anmelden slides off the top" on iPhone). Auto margins
          collapse to 0 on overflow, so the page scrolls normally instead. */}
      <div className="flex flex-1 flex-col items-center px-8 py-12 bg-pub-bg">
        <div className="w-full max-w-sm my-auto">
          {/* Mobile brand header — desktop shows the logo in the left panel */}
          <div className="lg:hidden mb-8">
            <RezaroLogo variant="light" size="md" />
          </div>
          <div className="flex items-center justify-between mb-10">
            <Link href="/backstage" className="text-[11px] font-medium text-pub-muted/50 hover:text-pub-accent transition-colors">
              {t.operatorPanel}
            </Link>
            <Link href={lp('/signup', lang)} className="text-[13px] text-pub-muted hover:text-pub-ink transition-colors">
              {t.noAccount}{' '}
              <span className="font-semibold text-pub-accent">{t.register}</span>
            </Link>
          </div>

          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-pub-muted/50 mb-1">{t.eyebrow}</p>
          <h1 className="text-3xl font-light text-pub-ink mb-7" style={{ fontFamily: 'var(--font-display)' }}>
            {t.greeting}
          </h1>

          <div className="space-y-2 mb-5">
            {SOCIAL.map(({ id, Icon }) => (
              <button key={id} type="button"
                onClick={() => handleSocial(id)}
                className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-pub-rule bg-pub-surface py-2.5 text-[13px] font-medium text-pub-ink hover:bg-pub-bg hover:border-pub-ink/20 transition-colors">
                <Icon />
                <span>{t.social_google}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-pub-rule" />
            <span className="text-[11px] text-pub-muted/50">{t.or}</span>
            <div className="flex-1 h-px bg-pub-rule" />
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-[12px] font-medium text-pub-ink">{t.emailLabel}</label>
              <input id="email" type="email" inputMode="email" enterKeyHint="next" required autoComplete="email" placeholder={t.emailPlaceholder}
                value={email} onChange={e => setEmail(e.target.value)}
                className="h-10 rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-[12px] font-medium text-pub-ink">{t.passwordLabel}</label>
                <a href="#" className="text-[12px] text-pub-muted hover:text-pub-accent transition-colors">{t.forgot}</a>
              </div>
              <div className="relative">
                <input id="password" type={showPw ? 'text' : 'password'} enterKeyHint="go" required autoComplete="current-password" placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="h-10 w-full rounded-lg border border-pub-rule bg-pub-surface px-3.5 pr-10 text-[13px] text-pub-ink placeholder:text-pub-muted/40 focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50 transition-colors" />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-pub-muted hover:text-pub-ink">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-[12px] text-red-600">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full h-10 rounded-full bg-pub-accent text-[13px] font-semibold text-white hover:bg-pub-accent-dark transition-colors disabled:opacity-60">
              {loading ? t.submitting : t.submit}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
